import { DragEndEvent, DragOverEvent, DragStartEvent } from '@dnd-kit/core';
import useLocalStorage from './useLocalStorage';

interface DragFunctions<T> {
  onDragStart?: (
    event: DragStartEvent,
    draggableState: T,
    setDraggableState: (newVal: T | ((prevVal: T) => T)) => void
  ) => void;
  onDragEnd?: (
    event: DragEndEvent,
    draggableState: T,
    setDraggableState: (newVal: T | ((prevVal: T) => T)) => void
  ) => void;
  onDragOver?: (
    event: DragOverEvent,
    draggableState: T,
    setDraggableState: (newVal: T | ((prevVal: T) => T)) => void
  ) => void;
}

export interface useOnDragResult<T> {
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragOver: (event: DragEndEvent) => void;
  draggableState: T;
  resetDraggableState: () => void;
}

const useOnDrag = <T>(
  draggableKey: string,
  defaultDraggableState: T,
  dragFunctions: DragFunctions<T> = {}
): useOnDragResult<T> => {
  const [draggableState, setDraggableState] = useLocalStorage(draggableKey, defaultDraggableState);
  const { onDragStart, onDragEnd, onDragOver } = dragFunctions;

  const handleDragStart = (event: DragStartEvent) => {
    document.body.style.cursor = 'grabbing';
    onDragStart?.(event, draggableState, setDraggableState);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    document.body.style.cursor = '';
    onDragEnd?.(event, draggableState, setDraggableState);
  };
  const handleDragOver = (event: DragOverEvent) => {
    document.body.style.cursor = 'grabbing';
    onDragOver?.(event, draggableState, setDraggableState);
  };

  const resetDraggableState = () => {
    setDraggableState(defaultDraggableState);
  };

  return {
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragOver: handleDragOver,
    draggableState,
    resetDraggableState,
  };
};

export default useOnDrag;
